'use client';

import {
  HumanVerificationChallenge,
  HumanVerificationChallengeHandle,
} from '@/components/human-verification-challenge';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import {
  leadGenInfoStore,
  selectLeadGenInfo,
  selectLeadGenInfoSubmitStatus,
  useLeadGenInfoStore,
} from '@/stores/leadGenInfoStore';
import { SubmitStatuses } from '@/util/storeUtilities';
import { LoaderCircle } from 'lucide-react';
import { useRef } from 'react';
import { equalityFn } from 'wired-store';

interface CollectLeadFormProps {
  cardBorder?: boolean;
  useAiStoreSearchRequest?: boolean;
  matchingPlansCount?: number | null;
}

export default function CollectLeadForm({
  cardBorder = true,
  useAiStoreSearchRequest = false,
  matchingPlansCount = null,
}: CollectLeadFormProps) {
  const humanVerificationRef = useRef<HumanVerificationChallengeHandle>(null);
  const { leadSubmitStatus, leadInfo } = useLeadGenInfoStore(
    (state) => ({
      leadSubmitStatus: selectLeadGenInfoSubmitStatus(state),
      leadInfo: selectLeadGenInfo(state),
    }),
    equalityFn,
  );

  const headline =
    matchingPlansCount && matchingPlansCount > 0
      ? matchingPlansCount === 1
        ? 'We found 1 plan matching your vehicle. Share a few details to get your quote.'
        : `We found ${matchingPlansCount} plans matching your vehicle. Share a few details to compare your quotes.`
      : "You're seconds away from your free warranty quote.";

  const supportText =
    matchingPlansCount && matchingPlansCount > 0
      ? 'Unlock real pricing, coverage details, and provider-backed options built around the vehicle you searched.'
      : 'Share your details and we will match them to the vehicle information you provided.';

  return (
    <div className="max-w-2xl mx-auto">
      <Card className={`p-6 text-center ${cardBorder ? '' : 'shadow-none border-none'}`}>
        <div className="mb-4">
          <h2 className="text-3xl font-bold mb-2">{headline}</h2>
          <p className="text-sm text-gray-600">{supportText}</p>
        </div>

        <div className="max-w-md mx-auto mb-4">
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Input
                  type="text"
                  placeholder="First Name"
                  className="w-full"
                  value={leadInfo.userInformation.firstName}
                  onChange={(e) => {
                    leadGenInfoStore.changeUserInformation({
                      firstName: e.target.value,
                    });
                  }}
                />
                {leadInfo.validationErrors.userInformation?.firstName ? (
                  <p className="text-red-500">{leadInfo.validationErrors.userInformation.firstName}</p>
                ) : null}
              </div>
              <div>
                <Input
                  type="text"
                  placeholder="Last Name"
                  className="w-full"
                  value={leadInfo.userInformation.lastName}
                  onChange={(e) => {
                    leadGenInfoStore.changeUserInformation({
                      lastName: e.target.value,
                    });
                  }}
                />
                {leadInfo.validationErrors.userInformation?.lastName ? (
                  <p className="text-red-500">{leadInfo.validationErrors.userInformation.lastName}</p>
                ) : null}
              </div>
            </div>
            <div>
              <Input
                type="email"
                placeholder="Email Address"
                className="w-full"
                value={leadInfo.userInformation.email}
                onChange={(e) => {
                  leadGenInfoStore.changeUserInformation({
                    email: e.target.value,
                  });
                }}
              />

              {leadInfo.validationErrors.userInformation?.email ? (
                <p className="text-red-500">{leadInfo.validationErrors.userInformation.email}</p>
              ) : null}
            </div>

            <div>
              <Input
                type="tel"
                placeholder="Phone Number"
                className="w-full"
                value={leadInfo.userInformation.phone}
                onChange={(e) => {
                  leadGenInfoStore.changeUserInformation({
                    phone: e.target.value,
                  });
                }}
              />
              {leadInfo.validationErrors.userInformation?.phone ? (
                <p className="text-red-500">{leadInfo.validationErrors.userInformation.phone}</p>
              ) : null}
            </div>

            <p className="text-xs py-4">
              Submitting this form allows our partner network to contact you with warranty offers.{' '}
              <a href="/privacy" target="_blank" className="underline">
                Privacy Policy
              </a>{' '}
              ·{' '}
              <a href="/terms" target="_blank" className="underline">
                Terms of Service
              </a>
            </p>

            <HumanVerificationChallenge ref={humanVerificationRef} />

            <div className="flex items-center">
              <Button
                className="w-full bg-[#00B67A] hover:bg-[#00A067]"
                disabled={leadSubmitStatus.submitStatus === SubmitStatuses.SUBMITTING}
                onClick={async () => {
                  if (!humanVerificationRef.current?.verify()) {
                    return;
                  }

                  await leadGenInfoStore.submitLeadInfo(useAiStoreSearchRequest);
                }}
              >
                {leadSubmitStatus.submitStatus === SubmitStatuses.SUBMITTING ? (
                  <LoaderCircle className="icon-spin" style={{ width: '30px', height: '30px' }} strokeWidth={2} />
                ) : (
                  <span className="inline-flex items-center">Submit</span>
                )}
              </Button>
            </div>
          </div>
        </div>
      </Card>
    </div>
  );
}
